import { fetch } from 'src/service/api/fetcher'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInfiniteQueryOptionInterface, useInfiniteQueryReturnData } from 'src/types/react-query'
import useIntersectionObserver from 'src/hooks/useObserver.hook'
import { CMDTYPES } from 'src/service/constant/cmdtypes'
import {
  AlwaysDiscountCouponListSpace,
  CouponListByBrandSpace,
  GiftedCouponListSpace,
  PurchaseHistoryListSpace,
  ResponseDataType,
  SearchSpace,
  SelfRegisterCouponListSpace,
  WalletCouponSpace,
} from 'src/types/api'
import { bottomSheetSortType } from 'src/components/layout/MainLayout/MainBottomSheet/_components/MainBottomSheetHeader'
import { AlwaysPageCouponSortOrderType } from 'src/pages/AlwaysDiscounCouponPage'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useInfiniteScrollQuery = <_T, U>(
  option: useInfiniteQueryOptionInterface<U>,
): useInfiniteQueryReturnData<U> => {
  const {
    data, // 💡 data.pages를 갖고 있는 배열
    fetchNextPage,
    hasNextPage,
    isSuccess,
    isPending,
    refetch,
  } = useInfiniteQuery({ ...option, networkMode: 'offlineFirst' })

  const { setTarget } = useIntersectionObserver({
    hasNextPage,
    fetchNextPage,
  })

  return {
    length: option.length,
    data: data,
    refetch,
    isSuccess,
    isPending,
    setTarget,
  }
}

//상시할인 쿠폰 리스트
const alwaysCouponListQueryOption = (
  Order: AlwaysPageCouponSortOrderType,
  CategoryNumber: number,
) => ({
  queryKey: ['always_coupon_list', `${Order}`, `${CategoryNumber}`],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<AlwaysDiscountCouponListSpace.Request, AlwaysDiscountCouponListSpace.Response>(
      CMDTYPES.AlwaysCouponListByCategory,
      {
        Offset: String(pageParam),
        Length: '20',
        Order: Order,
        CategoryNumber,
      },
    ),
  initialPageParam: 0,
  staleTime: 1000 * 60,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: (
    { Body }: ResponseDataType<AlwaysDiscountCouponListSpace.Response>,
    data: any,
  ) => {
    const { Offset, CommonCoupon } = Body
    return !!CommonCoupon ? Number(Offset) + 20 : null
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})

//검색 결과 쿠폰 리스트
const searchSubmitQueryOption = (word: string) => ({
  queryKey: ['search_submit', word],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<SearchSpace.Request, SearchSpace.Response>(CMDTYPES.SearchSubmit, {
      Offset: String(pageParam),
      Length: '20',
      Word: word,
    }),
  initialPageParam: 0,
  staleTime: 1000 * 60,
  length: 20,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: ({ Body }: ResponseDataType<SearchSpace.Response>, data: any) => {
    const { Offset, CouponSearch } = Body
    return !!CouponSearch ? Number(Offset) + 20 : null
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})

//구매내역 쿠폰 리스트
const purchaseCouponListQueryOption = (type: 0 | 1, brandNumber: number, tel: string) => ({
  queryKey: ['purchase_coupon_list', `${type}`, `${brandNumber}`, tel],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<PurchaseHistoryListSpace.Request, PurchaseHistoryListSpace.Response>(
      CMDTYPES.PurchaseCouponList,
      {
        Offset: String(pageParam),
        Length: '20',
        Type: type,
        BrandNumber: brandNumber,
      },
    ),
  initialPageParam: 0,
  staleTime: 1000,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: ({ Body }: ResponseDataType<PurchaseHistoryListSpace.Response>) => {
    const { Offset, PurchaseList } = Body
    return !!PurchaseList ? Number(Offset) + 20 : null
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})
// 직접등록 쿠폰 리스트
const selfRegisterCouponListQueryOption = () => ({
  queryKey: ['self_register_coupon'],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<SelfRegisterCouponListSpace.Request, SelfRegisterCouponListSpace.Response>(
      CMDTYPES.SelfRegisterCouponList,
      {
        Offset: String(pageParam),
        Length: '20',
      },
    ),
  initialPageParam: 0,
  cacheTime: 0,
  staleTime: 0,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: ({ Body }: ResponseDataType<SelfRegisterCouponListSpace.Response>) => {
    const { Offset, CouponSelf } = Body
    if (!!CouponSelf) {
      return Number(Offset) + 20
    } else {
      return null
    }
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})

//보유 쿠폰 리스트
const walletCouponQueryOption = (order: bottomSheetSortType) => ({
  queryKey: ['wallet_coupon_list', `${order}`],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<WalletCouponSpace.Request, WalletCouponSpace.Response>(
      CMDTYPES.walletCouponList,
      {
        Order: order,
        Offset: String(pageParam),
        Length: '10',
      },
    ),
  initialPageParam: 0,
  cacheTime: 0,
  staleTime: 1000 * 5,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: ({ Body }: ResponseDataType<WalletCouponSpace.Response>) => {
    const { Offset, CouponList } = Body
    if (!!CouponList) {
      return Number(Offset) + 10
    } else {
      return null
    }
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})

//브랜드별 쿠폰 리스트
const couponListByBrandQueryOption = ({
  BrandNumber,
  Order,
}: Pick<CouponListByBrandSpace.Request, 'BrandNumber' | 'Order'>) => ({
  queryKey: ['coupon_list_by_brand', `${Order}`, `${BrandNumber}`],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<CouponListByBrandSpace.Request, CouponListByBrandSpace.Response>(
      CMDTYPES.CouponListByBrand,
      {
        Order: Order,
        BrandNumber: BrandNumber,
        Offset: String(pageParam),
        Length: '20',
      },
    ),
  initialPageParam: 0,
  cacheTime: 0,
  staleTime: 0,
  length: 20,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: ({ Body }: ResponseDataType<CouponListByBrandSpace.Response>) => {
    const { Offset, BrandCouponList } = Body
    if (!!BrandCouponList && BrandCouponList.length % 20 === 0) {
      return Number(Offset) + 20
    } else {
      return null
    }
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})

//선물한 쿠폰 리스트
const giftedCouponListQueryOption = () => ({
  queryKey: ['gifted_coupon_list'],
  queryFn: async ({ pageParam = 0 }) =>
    await fetch.post<GiftedCouponListSpace.Request, GiftedCouponListSpace.Response>(
      CMDTYPES.GiftedCouponList,
      {
        Offset: String(pageParam),
        Length: '20',
      },
    ),
  initialPageParam: 0,
  staleTime: 1000,
  //요청이 완료 된 후에 가장 최신 응답 값으로 몇 페이지인지 계산 해서 return 함
  getNextPageParam: (
    { Body }: ResponseDataType<GiftedCouponListSpace.Response>,
    allPages: ResponseDataType<GiftedCouponListSpace.Response>[],
  ) => {
    const { GiftList } = Body
    if (!!GiftList) {
      return allPages.length * 20
    } else {
      return null
    }
  },
  meta: { errorMessage: `무한 스크롤 에러` },
})

export {
  alwaysCouponListQueryOption,
  searchSubmitQueryOption,
  couponListByBrandQueryOption,
  purchaseCouponListQueryOption,
  selfRegisterCouponListQueryOption,
  walletCouponQueryOption,
  giftedCouponListQueryOption,
}
